/**
 * Apple In-App Purchase Service (Frontend)
 * 
 * Uses native StoreKit plugin (StoreKitPlugin.swift) via Capacitor bridge.
 * This service is ONLY used on iOS. Android/Web use Stripe.
 * 
 * Flow:
 * 1. User taps "Buy" → calls native StoreKit via Capacitor
 * 2. iOS shows native purchase dialog
 * 3. Apple processes payment → returns transaction + JWS
 * 4. Send transaction data to our backend for validation
 * 5. Backend validates and updates credits/subscription
 */

import { registerPlugin } from '@capacitor/core';
import axios from 'axios';
import { isIOS } from './platform.service';

const API_URL = import.meta.env.VITE_API_URL || '';

// Register the native StoreKit plugin
interface StoreKitPluginInterface {
  purchase(options: { productId: string }): Promise<{
    success: boolean;
    transactionId?: string;
    originalTransactionId?: string;
    productId?: string;
    receiptData?: string;
    jwsRepresentation?: string;
    expiresDateMs?: number;
    purchaseDateMs?: number;
    cancelled?: boolean;
    pending?: boolean;
    error?: string;
  }>;
  getProducts(options: { productIds: string[] }): Promise<{
    products: Array<{
      id: string;
      displayName: string;
      description: string;
      price: number;
      displayPrice: string;
      type: string;
    }>;
  }>;
  restorePurchases(): Promise<{
    success: boolean;
    receiptData?: string;
    jwsRepresentation?: string;
    hasActiveSubscription?: boolean;
    activeSubscription?: any;
  }>;
  getReceipt(): Promise<{ receiptData: string }>;
}

const StoreKit = registerPlugin<StoreKitPluginInterface>('StoreKit');

/**
 * Get auth headers
 */
function getAuthHeaders() {
  const token = localStorage.getItem('accessToken');
  return { Authorization: `Bearer ${token}` };
}

/**
 * Purchase a subscription through Apple IAP
 */
export async function purchaseSubscription(productId: string): Promise<{
  success: boolean;
  tier?: string;
  credits?: number;
  error?: string;
}> {
  if (!isIOS()) {
    return { success: false, error: 'Apple IAP is only available on iOS' };
  }

  try {
    // Step 1: Call native StoreKit purchase
    console.log('[Apple IAP] Starting purchase for:', productId);
    const purchaseResult = await StoreKit.purchase({ productId });

    if (purchaseResult.cancelled) {
      return { success: false, error: 'Achiziția a fost anulată.' };
    }

    if (purchaseResult.pending) {
      return { success: false, error: 'Achiziția este în așteptare (necesită aprobare).' };
    }

    if (!purchaseResult.success) {
      return { success: false, error: purchaseResult.error || 'Achiziția nu a reușit.' };
    }

    // Step 2: Validate with our backend - send both receipt and JWS transaction data
    console.log('[Apple IAP] Purchase successful, validating with backend...');
    console.log('[Apple IAP] Has receipt:', !!purchaseResult.receiptData);
    console.log('[Apple IAP] Has JWS:', !!purchaseResult.jwsRepresentation);
    console.log('[Apple IAP] Transaction ID:', purchaseResult.transactionId);
    
    const response = await axios.post(
      `${API_URL}/api/subscriptions/apple/validate`,
      {
        receiptData: purchaseResult.receiptData || '',
        productId: purchaseResult.productId || productId,
        // StoreKit 2 transaction data (used when receipt is empty)
        transactionId: purchaseResult.transactionId,
        originalTransactionId: purchaseResult.originalTransactionId,
        jwsRepresentation: purchaseResult.jwsRepresentation,
        expiresDateMs: purchaseResult.expiresDateMs,
        purchaseDateMs: purchaseResult.purchaseDateMs,
      },
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error: any) {
    console.error('[Apple IAP] Purchase error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Eroare la achiziție',
    };
  }
}

/**
 * Purchase extra credits through Apple IAP
 */
export async function purchaseCredits(productId: string): Promise<{
  success: boolean;
  credits?: number;
  error?: string;
}> {
  if (!isIOS()) {
    return { success: false, error: 'Apple IAP is only available on iOS' };
  }

  try {
    console.log('[Apple IAP] Starting credits purchase for:', productId);
    const purchaseResult = await StoreKit.purchase({ productId });

    if (purchaseResult.cancelled) {
      return { success: false, error: 'Achiziția a fost anulată.' };
    }

    if (purchaseResult.pending) {
      return { success: false, error: 'Achiziția este în așteptare.' };
    }

    if (!purchaseResult.success) {
      return { success: false, error: purchaseResult.error || 'Achiziția nu a reușit.' };
    }

    // Validate with backend
    const response = await axios.post(
      `${API_URL}/api/subscriptions/apple/validate`,
      {
        receiptData: purchaseResult.receiptData || '',
        productId: purchaseResult.productId || productId,
        transactionId: purchaseResult.transactionId,
        originalTransactionId: purchaseResult.originalTransactionId,
        jwsRepresentation: purchaseResult.jwsRepresentation,
        expiresDateMs: purchaseResult.expiresDateMs,
        purchaseDateMs: purchaseResult.purchaseDateMs,
      },
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error: any) {
    console.error('[Apple IAP] Credits purchase error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Eroare la achiziție',
    };
  }
}

/**
 * Restore previous purchases
 * Apple requires this button to be available in the app
 */
export async function restorePurchases(): Promise<{
  success: boolean;
  hasActiveSubscription?: boolean;
  tier?: string;
  message?: string;
  error?: string;
}> {
  if (!isIOS()) {
    return { success: false, error: 'Restore is only available on iOS' };
  }

  try {
    console.log('[Apple IAP] Restoring purchases...');
    const restoreResult = await StoreKit.restorePurchases();

    if (!restoreResult.success) {
      return {
        success: false,
        error: 'Nu s-au putut restaura achizițiile. Asigură-te că ești logat cu Apple ID-ul corect.',
      };
    }

    // Send to backend for validation - try receipt first, then JWS
    const response = await axios.post(
      `${API_URL}/api/subscriptions/apple/restore`,
      { 
        receiptData: restoreResult.receiptData || '',
        jwsRepresentation: restoreResult.jwsRepresentation || '',
        activeSubscription: restoreResult.activeSubscription,
      },
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error: any) {
    console.error('[Apple IAP] Restore error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Eroare la restaurare',
    };
  }
}

/**
 * Get Apple products with prices from App Store
 */
export async function getAppleProducts(productIds: string[]) {
  if (!isIOS()) return null;

  try {
    const result = await StoreKit.getProducts({ productIds });
    return result.products;
  } catch (error) {
    console.error('[Apple IAP] Error fetching products:', error);
    return null;
  }
}

/**
 * Get Apple product catalog from backend
 */
export async function getAppleProductCatalog() {
  try {
    const response = await axios.get(`${API_URL}/api/subscriptions/apple/products`);
    return response.data;
  } catch (error) {
    console.error('[Apple IAP] Error fetching product catalog:', error);
    return null;
  }
}
